import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Mail as MailIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const PendingInvitations = ({ invitations, onInvitationUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const acceptMutation = useMutation(
    async (jointAccountId) => {
      const response = await apiClient.post(`/api/joint-accounts/${jointAccountId}/accept`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jointAccounts');
        if (onInvitationUpdate) onInvitationUpdate();
      }
    }
  );

  const rejectMutation = useMutation(
    async (jointAccountId) => {
      const response = await apiClient.post(`/api/joint-accounts/${jointAccountId}/reject`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jointAccounts');
        if (onInvitationUpdate) onInvitationUpdate();
      }
    }
  );

  const handleAccept = (jointAccountId) => {
    acceptMutation.mutate(jointAccountId);
  };

  const handleReject = (jointAccountId) => {
    rejectMutation.mutate(jointAccountId);
  };


  
  if (!invitations || invitations.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ 
        color: '#FFFFFF', 
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <MailIcon sx={{ color: '#F7931A' }} />
        Pending Invitations ({invitations.length})
      </Typography>

      <List sx={{ width: '100%' }}>
        {invitations.map((account) => {
          const member = account.members.find(m => m.status === 'pending');
          const inviter = account.members.find(m => m.user_id === account.created_by);
          
          return (
            <Card
              key={account.id}
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid #F7931A',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#FFB800',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(247, 147, 26, 0.3)'
                }
              }}
            >
              <ListItem>
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: '#F7931A',
                      width: 56,
                      height: 56
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {account.account_name}
                      </Typography>
                      <Chip
                        label="Pending"
                        color="warning"
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Account #{account.account_number}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Invited by: {inviter?.user?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#F7931A', fontWeight: 600 }}>
                        Role: {member?.role || 'member'}
                      </Typography>
                      {account.description && (
                        <Typography variant="body2" sx={{ color: '#6C7293', mt: 1 }}>
                          {account.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1 
                }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={() => handleAccept(account.id)}
                    disabled={acceptMutation.isLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                      }
                    }}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={() => handleReject(account.id)}
                    disabled={rejectMutation.isLoading}
                    sx={{
                      borderColor: '#F84960',
                      color: '#F84960',
                      '&:hover': {
                        borderColor: '#DC2626',
                        backgroundColor: 'rgba(248, 73, 96, 0.1)'
                      }
                    }}
                  >
                    Reject
                  </Button>
                </Box>
              </ListItem>
            </Card>
          );
        })}
      </List>
    </Box>
  );
};

export default PendingInvitations; 